import { useAuth0 } from '@auth0/auth0-react';
import Storage, { StorageKeys } from 'lane-shared/helpers/Storage';
import { useAuth0Enabled } from 'lane-shared/hooks/auth0/useAuth0Enabled';
import { useAuth0AccessToken } from 'lane-shared/hooks/auth0/useAuth0AccessToken';
import { useAuth0ConsolidateAuthTokenStorage } from 'lane-shared/hooks/auth0/useAuth0ConsolidateAuthTokenStorage';
import { GetAuthTokenResponse } from 'lane-shared/types/auth0/getAuthTokenResponseType';
import { NotFoundError } from 'activate-errors';
import * as Sentry from '@sentry/browser';

export function useGetAuthToken(): GetAuthTokenResponse {
  const { getAccessTokenSilently, isLoading, isAuthenticated } = useAuth0();
  const isAuth0Enabled = useAuth0Enabled();
  const useAccessToken = useAuth0AccessToken();
  const useAuth0TokenStorage = useAuth0ConsolidateAuthTokenStorage();

  const getAuthToken: GetAuthTokenResponse = async () => {
    try {
      if (!isAuth0Enabled || !useAuth0TokenStorage) {
        return await Storage.getItem(StorageKeys.AUTH_TOKEN);
      }

      if (isLoading) {
        return null;
      }

      if (!isAuthenticated) {
        return null;
      }

      if (useAccessToken) {
        return await getAccessTokenSilently({
          timeoutInSeconds: 15,
        });
      }

      const auth0Response = await getAccessTokenSilently({
        detailedResponse: true,
        timeoutInSeconds: 15,
      });

      return auth0Response.id_token;
    } catch (error) {
      if (!(error instanceof NotFoundError)) {
        Sentry.captureException(error);
      }

      return null;
    }
  };

  return getAuthToken;
}
